import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useBrand } from '../context/BrandContext';
import { useUser } from '../context/UserContext';
import { Calendar, Clock, Users, ArrowRight, Check, X, Shield, Package, Waves, User, Mail, Phone, AlertCircle, Loader2, Cloud, Wind, ThermometerSun, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { doc, collection, setDoc } from 'firebase/firestore';
import { db, registerWithEmail, loginWithEmail, updateAuthProfile } from '../firebase';

interface UnifiedBookingCalendarProps {
  type?: 'night-raid' | 'coaching' | 'team-building' | 'baja' | 'group-lesson' | 'lake-crossing' | 'event' | 'expedition' | string;
  title?: string;
  subtitle?: string;
  description?: string;
  theme?: 'light' | 'dark';
}

export default function UnifiedBookingCalendar({
  type,
  title = "Expedition Calendar",
  subtitle = "SCHEDULE",
  description = "Select an available window to join our next session.",
  theme = 'light'
}: UnifiedBookingCalendarProps) {
  const navigate = useNavigate();
  const { expeditionSlots, bookExpedition, userBookings, content, loadingSlots } = useBrand();
  const { user: currentUser, profile, isAdmin } = useUser();
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [bookingStep, setBookingStep] = useState<'info' | 'details' | 'waiver' | 'redirect'>('info');
  const modalScrollRef = useRef<HTMLDivElement>(null);

  // Body scroll lock when modal is open
  useEffect(() => {
    if (selectedSlotId) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [selectedSlotId]);

  useEffect(() => {
    if (modalScrollRef.current) {
      modalScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [bookingStep]);

  const [bookingStatus, setBookingStatus] = useState<{ status: 'idle' | 'loading' | 'success' | 'error', message?: string }>({ status: 'idle' });
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: currentUser?.displayName || '',
    email: currentUser?.email || '',
    phone: '',
    password: '',
    corporateName: '',
    maritimeRiskAccepted: false,
    physicalStandardsAccepted: false,
    meteorologicalAdaptabilityAccepted: false,
    waiverAccepted: false,
    giftCode: '',
    requiresGear: false,
    intensityLevel: ''
  });
  const [useGiftCard, setUseGiftCard] = useState(false);
  const [weatherData, setWeatherData] = useState<Record<string, any>>({});

  const todayStr = new Date().toISOString().split('T')[0];

  const processedSlots = type
    ? expeditionSlots.filter(s => {
      if (s.status === 'cancelled' || s.date < todayStr) return false;
      if (type === 'expedition') {
        return s.type === 'expedition' || s.type === 'lake-crossing' || s.type === 'baja' || s.type === 'night-raid';
      }
      if (type === 'coaching') {
        return s.type === 'coaching' || s.type === 'group-lesson' || s.type === 'night-raid';
      }
      return s.type === type;
    })
    : expeditionSlots.filter(s => s.status !== 'cancelled' && s.date >= todayStr);

  const baseSlots = processedSlots.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const datesDependency = Array.from(new Set(baseSlots.map(s => s.date))).join(',');
  useEffect(() => {
    const uniqueDates = Array.from(new Set(baseSlots.map(s => s.date)));

    uniqueDates.forEach(date => {
      if (weatherData[date]) return;

      const targetDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const diffDays = Math.ceil(
        (targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffDays >= 0 && diffDays <= 7) {
        setWeatherData(prev => {
          if (prev[date]) return prev;
          return { ...prev, [date]: { loading: true } };
        });

        fetch(`/api/weather?date=${date}`)
          .then(res => res.json())
          .then(data => {
            setWeatherData(prev => ({ ...prev, [date]: data }));
          })
          .catch(err => {
            console.error("Failed to fetch weather", err);
            setWeatherData(prev => ({ ...prev, [date]: { error: true } }));
          });
      } else if (diffDays > 7) {
        setWeatherData(prev => {
          if (prev[date]) return prev;
          return { ...prev, [date]: { status: "pending" } };
        });
      }
    });
  }, [baseSlots.length, type, datesDependency]);

  // Always append these two premium teasers to the bottom of the open calendar.
  // This allows the user to see them, but it enforces the Elite limitation visually.
  const slots = baseSlots;
  const selectedSlot = expeditionSlots.find(s => s.id === selectedSlotId); const isCorporateMember = profile?.automationTags?.includes('CORP_MEMBER');
  const isEliteMember = profile?.membershipTier === 'Elite Status' || profile?.automationTags?.includes('ELITE_RANK');
  const isSyndicateMember = profile?.membershipTier === 'Syndicate Member' || profile?.automationTags?.includes('SYNDICATE_MEMBER');

  const getDuration = (slotType: string) => {
    switch (slotType) {
      case 'baja': return '10 Day';
      case 'lake-crossing': return '14 Hour';
      case 'event': return 'Variable';
      case 'team-building': return '4 Hour';
      case 'coaching': return '2 Hour';
      case 'group-lesson': return '3 Hour';
      case 'night-raid': return '4 Hour';
      default: return '4 Hour';
    }
  };

  const getSlotTheme = (slot: any, weather: any) => {
    const noDataTheme = {
      level: 'green',
      card: 'bg-white border-ows-teal/5 hover:border-ows-teal/30 hover:shadow-2xl hover:shadow-ows-teal/5',
      text: 'text-ows-dark',
      textDim: 'text-ows-dark/40',
      button: 'bg-ows-dark text-white hover:bg-ows-teal',
      pill: 'bg-ows-dark/5 text-ows-dark',
      levelLabel: 'SCHEDULED', indicator: null,
      barBg: 'bg-ows-dark/5', barFill: 'bg-ows-teal'
    };

    if (slot.status === 'full') {
      return {
        level: 'green',
        card: 'bg-ows-grey border-ows-grey opacity-50 text-ows-dark',
        text: 'text-ows-dark',
        textDim: 'text-ows-dark/40',
        button: 'bg-ows-orange text-white hover:bg-ows-orange/80',
        pill: 'bg-ows-dark/5 text-ows-dark',
        levelLabel: 'FULL CAPACITY',
        indicator: null,
        barBg: 'bg-ows-dark/5',
        barFill: 'bg-ows-orange'
      };
    }

    if (!weather || weather.status !== 'success') {
      return noDataTheme;
    }

    let windScore = 0;
    const windKmh = weather.windSpeed * 3.6;
    if (windKmh >= 15) windScore = 1;
    if (windKmh >= 25) windScore = 2;
    if (windKmh >= 35) windScore = 3;
    if (windKmh >= 50) windScore = 4;

    let waveScore = 0;
    const wave = weather.waveHeight;
    if (wave >= 0.4) waveScore = 1;   // blue
    if (wave >= 0.7) waveScore = 2;   // brown
    if (wave >= 1.2) waveScore = 3;   // black
    if (wave >= 2.0) waveScore = 4;   // double black

    let score = Math.max(windScore, waveScore);

    if (slot.type === 'night-raid') {
      score += 1;
    }

    let level = 'green';
    if (score <= 0) level = 'green';
    else if (score === 1) level = 'blue';
    else if (score === 2) level = 'brown';
    else if (score === 3) level = 'black';
    else level = 'red';

    switch (level) {
      case 'green': return {
        level, card: 'bg-[#eefcf4] border-green-500/20 hover:border-green-500/40 hover:shadow-2xl hover:shadow-green-900/10',
        text: 'text-green-950', textDim: 'text-green-800/70',
        button: 'bg-green-950 text-white hover:bg-green-900',
        pill: 'bg-green-200/50 text-green-800 border-green-300/40 font-bold',
        levelLabel: 'GREEN LEVEL', indicator: '🟢', barBg: 'bg-green-200/50', barFill: 'bg-green-600'
      };
      case 'blue': return {
        level, card: 'bg-[#f0f9ff] border-blue-500/20 hover:border-blue-500/40 hover:shadow-2xl hover:shadow-blue-900/10',
        text: 'text-blue-950', textDim: 'text-blue-800/70',
        button: 'bg-blue-950 text-white hover:bg-blue-900',
        pill: 'bg-blue-200/50 text-blue-800 border-blue-300/40 font-bold',
        levelLabel: 'BLUE LEVEL', indicator: '🔵', barBg: 'bg-blue-200/50', barFill: 'bg-blue-600'
      };
      case 'brown': return {
        level, card: 'bg-[#4a2e15] border-amber-500/20 hover:border-amber-500/40 hover:shadow-2xl hover:shadow-amber-900/40',
        text: 'text-white', textDim: 'text-white/60',
        button: 'bg-white text-[#4a2e15] hover:bg-amber-600 hover:text-white',
        pill: 'bg-amber-500/20 text-amber-300 border-amber-500/30 font-bold',
        levelLabel: 'BROWN LEVEL', indicator: '🟤', barBg: 'bg-amber-500/20', barFill: 'bg-amber-500'
      };
      case 'black': return {
        level, card: 'bg-[#151515] border-white/10 hover:border-white/30 hover:shadow-2xl hover:shadow-white/5',
        text: 'text-white', textDim: 'text-white/40',
        button: 'bg-white text-[#0A0A0A] hover:bg-gray-200',
        pill: 'bg-white/10 text-white/70 border-white/20 font-bold',
        levelLabel: 'BLACK LEVEL', indicator: '⚫', barBg: 'bg-white/10', barFill: 'bg-gray-400'
      };
      case 'red': return {
        level, card: 'bg-[#050505] border-red-500/30 hover:border-red-500/60 hover:shadow-2xl hover:shadow-red-900/40 relative',
        text: 'text-white', textDim: 'text-red-200/50',
        button: 'bg-red-600 text-white hover:bg-red-700 hover:border-red-500',
        pill: 'bg-red-500/20 text-red-300 border-red-500/30 font-bold',
        levelLabel: 'RED LEVEL', indicator: '🔴', barBg: 'bg-red-500/20', barFill: 'bg-red-500'
      };
      default: return {
        level, card: 'bg-ows-grey border-ows-teal/5 hover:border-ows-teal/30 hover:shadow-2xl hover:shadow-ows-teal/5',
        text: 'text-ows-dark', textDim: 'text-ows-dark/40',
        button: 'bg-ows-dark text-white hover:bg-ows-teal',
        pill: 'bg-ows-dark/5 text-ows-dark font-bold',
        levelLabel: 'GREEN LEVEL', indicator: '🟢', barBg: 'bg-ows-dark/5', barFill: 'bg-ows-teal'
      };
    }
  };

  const calculateDiscountedPrice = (basePrice: number, slotType: string) => {
    if (isEliteMember) {
      if (slotType === 'baja' || slotType === 'team-building' || slotType === 'corporate') return Math.round(basePrice * 0.8); // 20% off
      return 0; // Lake and other local training classes / group coaching are free
    }

    if (isCorporateMember) {
      // Free/corporate members pay full price under the new strict rule that "Only elite members get free"
      return basePrice;
    }

    if (isSyndicateMember) {
      // Syndicate members get 50% off for local trainings & coaching sessions
      if (slotType === 'group-lesson' || slotType === 'coaching' || slotType === 'lake-crossing' || slotType === 'night-exped' || slotType === 'day' || slotType === 'night') {
        return Math.round(basePrice * 0.5); // 50% off
      }
      return basePrice;
    }

    // Free members pay 100% full price
    return basePrice;
  };

  const getMembershipBadge = () => {
    if (isCorporateMember) return { label: 'Corporate Fleet Rate', color: 'bg-ows-teal/10 text-ows-teal' };
    if (isEliteMember) return { label: 'Elite Pricing', color: 'bg-ows-orange/10 text-ows-orange' };
    if (isSyndicateMember) return { label: 'Syndicate Member Pricing', color: 'bg-white/10 text-white' };
    return null;
  };

  const isBooked = (slotId: string) => {
    return userBookings.some(b => b.slotId === slotId && b.status === 'confirmed');
  };

  const isWaitlisted = (slotId: string) => {
    return userBookings.some(b => b.slotId === slotId && b.status === 'waitlist');
  };

  const handleRedeemGiftCard = async (discountedPrice: number) => {
    if (!selectedSlotId || !selectedSlot || !currentUser) {
      throw new Error('Mission data or user identity missing.');
    }

    const response = await fetch('/api/redeem-gift-card', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: formData.giftCode.trim(),
        slotId: selectedSlotId,
        userId: currentUser.uid,
        serviceName: selectedSlot.type.replace('-', ' '),
        bookingType: selectedSlot.type,
        ...formData
      }),
    });

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Identity verify failed: Invalid or depleted gift code.');
    }

    return result;
  };

  const handleBook = async () => {
    if (!selectedSlotId || !selectedSlot) return;

    setBookingStatus({ status: 'loading', message: 'Authenticating...' });

    let bookingUserId = currentUser?.uid || 'guest';

    if (!currentUser && formData.password && formData.password.length >= 6) {
      try {
        try {
          const userCredential = await registerWithEmail(formData.email, formData.password);
          bookingUserId = userCredential.user.uid;
          await updateAuthProfile({ displayName: formData.name });

          await setDoc(doc(db, 'users', bookingUserId), {
            uid: bookingUserId,
            name: formData.name,
            email: formData.email,
            phone: formData.phone || '',
            role: 'member',
            membershipStatus: 'active',
            membershipTier: 'Prospect',
            createdAt: new Date().toISOString()
          });
        } catch (e: any) {
          if (e.code === 'auth/email-already-in-use') {
            try {
              const userCredential = await loginWithEmail(formData.email, formData.password);
              bookingUserId = userCredential.user.uid;
            } catch (loginErr: any) {
              console.warn("Credential check failed for existing user during booking. Falling back to guest checkout to prevent lock-out.", loginErr);
              bookingUserId = 'guest';
            }
          } else {
            console.warn("Registration error during booking. Falling back to guest checkout to prevent lock-out.", e);
            bookingUserId = 'guest';
          }
        }
      } catch (authError: any) {
        console.warn("Auth block error. Proceeding as guest.", authError);
        bookingUserId = 'guest';
      }
    } else if (!currentUser && formData.password && formData.password.length > 0 && formData.password.length < 6) {
      // Proceed as guest instead of raising a hard blocking error for short passwords
      console.warn("Password too short for account creation. Booking as guest.");
      bookingUserId = 'guest';
    }

    setBookingStatus({ status: 'loading', message: 'Processing...' });

    try {
      const discountedPrice = calculateDiscountedPrice(selectedSlot.price, selectedSlot.type);
      const isFull = selectedSlot.status === 'full' || selectedSlot.booked >= selectedSlot.capacity;

      // Branch 0: Waitlist
      if (isFull) {
        await bookExpedition(selectedSlotId, { ...formData, status: 'waitlist', userId: bookingUserId } as any);
        setBookingStatus({ status: 'success', message: 'Added to Waitlist.' });
        setTimeout(() => setSelectedSlotId(null), 3000);
        return;
      }

      let totalDue = discountedPrice;

      // One-on-one sessions (coaching) and corporate bookings (team-building) include the board. Group lessons/others have rental option.
      const isBoardIncluded = selectedSlot.type === 'coaching' || selectedSlot.type === 'team-building';
      if (formData.requiresGear && !isBoardIncluded) {
        const gearCost = isSyndicateMember ? 25 : 50;
        totalDue += gearCost;
      }

      totalDue = totalDue + (totalDue * 0.13); // with tax

      let giftAmountUsed = 0;

      // Branch 1: Gift Card Flow
      if (useGiftCard && formData.giftCode) {
        const validateRes = await fetch('/api/validate-gift-card', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: formData.giftCode.trim(), bookingType: selectedSlot.type }),
        });
        const validateData = await validateRes.json();

        if (!validateRes.ok || !validateData.success) {
          throw new Error(validateData.error || 'Invalid or depleted gift code.');
        }

        const giftBalance = validateData.amount;
        if (giftBalance >= totalDue) {
          // Fully covered
          const redeemRes = await fetch('/api/redeem-gift-card', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: formData.giftCode.trim(),
              slotId: selectedSlotId,
              userId: bookingUserId,
              serviceName: selectedSlot.type.replace('-', ' '),
              bookingType: selectedSlot.type,
              priceToDeduct: totalDue,
              ...formData
            }),
          });
          const redeemData = await redeemRes.json();
          if (!redeemRes.ok || !redeemData.success) {
            throw new Error(redeemData.error || 'Failed to process gift card.');
          }

          setBookingStatus({ status: 'success', message: 'Mission Authorized: Your gift card covered the total.' });
          setTimeout(() => setSelectedSlotId(null), 3000);
          return;
        } else {
          // Partially covered
          giftAmountUsed = giftBalance;
          totalDue = totalDue - giftBalance;
        }
      }

      // Branch 2: Complimentary Access (Ultimate Members or Free Events)
      if (totalDue <= 0 && !giftAmountUsed) {
        const freeRes = await fetch('/api/confirm-free-booking', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slotId: selectedSlotId,
            userId: bookingUserId,
            serviceName: selectedSlot.type.replace('-', ' '),
            date: selectedSlot.date,
            time: selectedSlot.time,
            clientName: currentUser?.displayName || formData.name,
            clientEmail: currentUser?.email || formData.email,
            clientPhone: formData.phone || 'N/A',
            ...formData
          }),
        });

        const freeData = await freeRes.json();
        if (!freeRes.ok || !freeData.success) {
          throw new Error(freeData.error || 'Failed to process complimentary booking.');
        }

        const successMessage = isEliteMember || isCorporateMember
          ? 'Clearance Verified: Session confirmed at zero cost.'
          : 'Session confirmed at no cost. Check your email for details.';
        setBookingStatus({ status: 'success', message: successMessage });
        setTimeout(() => setSelectedSlotId(null), 3000);
        return;
      }

      // Branch 3: Standard Stripe Checkout Flow
      setBookingStep('redirect');
      try {
        setBookingStatus({ status: 'loading', message: 'Initializing secure link...' });
        const pendingBookingRef = doc(collection(db, 'bookings'));
        const bookingId = pendingBookingRef.id;

        await setDoc(pendingBookingRef, {
          userId: bookingUserId,
          clientName: currentUser?.displayName || formData.name,
          clientEmail: currentUser?.email || formData.email,
          clientPhone: formData.phone || 'N/A',
          userEmail: currentUser?.email || formData.email,
          userName: currentUser?.displayName || formData.name,
          userPhone: formData.phone || 'N/A',
          type: selectedSlot.type || 'Expedition',
          serviceName: selectedSlot.type.replace('-', ' '),
          date: selectedSlot.date,
          time: selectedSlot.time,
          slotId: selectedSlotId,
          status: 'pending',
          paymentId: 'pending',
          amountTotal: totalDue,
          createdAt: new Date().toISOString(),
          timestamp: new Date().toISOString(),
          waiverId: bookingId,
          waiverSigned: !!profile?.waiverSigned || formData.waiverAccepted === true,
          corporateName: formData.corporateName || null,
          maritimeRiskAccepted: formData.maritimeRiskAccepted === true,
          physicalStandardsAccepted: formData.physicalStandardsAccepted === true,
          meteorologicalAdaptabilityAccepted: formData.meteorologicalAdaptabilityAccepted === true,
          requiresGear: formData.requiresGear === true,
          intensityLevel: formData.intensityLevel || null,
          giftCodeUsed: (useGiftCard && formData.giftCode) ? formData.giftCode.trim() : null,
          giftAmountUsed: giftAmountUsed
        });

        let response;
        let retries = 3;
        while (retries > 0) {
          try {
            response = await fetch('/api/create-service-checkout-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                serviceName: selectedSlot.type.replace('-', ' '),
                price: totalDue, // Pass adjusted totalDue
                tax: 0, // already baked into totalDue
                date: selectedSlot.date,
                time: selectedSlot.time,
                userId: bookingUserId,
                slotId: selectedSlotId,
                bookingId: bookingId,
                giftCodeToDeduct: (useGiftCard && formData.giftCode) ? formData.giftCode.trim() : null,
                giftAmountToDeduct: giftAmountUsed,
                ...formData
              })
            });
            if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 404)) break;
          } catch (e) {
            // ignore network err in loop
          }
          retries--;
          if (retries > 0) await new Promise(res => setTimeout(res, 1500));
        }

        if (!response || !response.ok) {
          if (response && response.status === 404) {
            throw new Error("Protocol Failure 404: Endpoint unreachable. Please check if an ad-blocker or strict proxy is interfering.");
          }
          const errData = response ? await response.json().catch(() => ({})) : {};
          throw new Error(errData.error || `Protocol Failure: ${response?.status || 'Network Disconnected'}`);
        }

        const data = await response.json();
        if (data.url) {
          setCheckoutUrl(data.url);

          // Proper delay before redirect
          setTimeout(() => {
            const stripeUrl = data.url;
            console.log("Initiating booking redirect:", stripeUrl);
            window.location.href = stripeUrl;
          }, 100);
        } else {
          throw new Error(data.error || 'Checkout session created but no URL returned.');
        }
      } catch (error: any) {
        console.error("Payment Protocol Failure:", error);

        // Elite/Member Fallback: If price is $0, allow direct booking even if Stripe fails
        if (discountedPrice === 0) {
          try {
            const freeRes = await fetch('/api/confirm-free-booking', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                slotId: selectedSlotId,
                userId: bookingUserId,
                serviceName: selectedSlot.type.replace('-', ' '),
                date: selectedSlot.date,
                time: selectedSlot.time,
                clientName: currentUser?.displayName || formData.name,
                clientEmail: currentUser?.email || formData.email,
                clientPhone: formData.phone || 'N/A',
                ...formData
              }),
            });
            const freeData = await freeRes.json();
            if (!freeRes.ok || !freeData.success) {
              throw new Error(freeData.error || 'Failed fallback bypass protocol');
            }

            setBookingStatus({ status: 'success', message: 'Mission Authorized: Secure bypass protocol successful.' });
            setTimeout(() => setSelectedSlotId(null), 3000);
          } catch (bookError: any) {
            console.error("Fallback Booking Failure:", bookError);
            setBookingStatus({ status: 'error', message: `Identity Verification Failed: ${bookError.message}` });
          }
          return;
        }

        setBookingStatus({ status: 'error', message: `Terminal Offline: ${error.message || 'Connection failed.'}` });
      }
    } catch (error: any) {
      console.error("Critical Booking Failure:", error);
      setBookingStatus({ status: 'error', message: error.message || "Unknown protocol failure" });
      if (useGiftCard) setBookingStep('details');
    }
  };

  return (
    <div className="w-full">
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-3 text-ows-teal mb-4">
          <Calendar className="h-5 w-5" />
          <span className="text-[10px] sm:text-xs font-bold tracking-[0.4em] uppercase font-mono">
            {subtitle}
          </span>
        </div>
        <h2 className={`text-xl md:text-2xl font-bold uppercase tracking-[0.1em] mb-4 ${theme === 'dark' ? 'text-white' : 'text-ows-dark'}`}>
          Available year-round. Book your date.
        </h2>
        {description && (
          <p className={`max-w-xl mx-auto font-light tracking-wide text-sm ${theme === 'dark' ? 'text-white/40' : 'text-ows-dark/40'}`}>
            {description}
          </p>
        )}

        {type === 'team-building' && (
          <div className="max-w-2xl mx-auto py-8 border-y border-ows-teal/10">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0D3A40] mb-4 font-sans italic">SESSION // THE ISLANDS LOOP</p>
            <p className="text-[11px] text-ows-dark/60 font-light leading-relaxed uppercase tracking-wider">
              "A 4-hour high-performance hybrid expedition departing from Queens Quay West Marina through the Toronto Islands. Fully guided by a professional instructor. Maximum cohort capacity: 8 operators."
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {loadingSlots ? (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className="p-10 rounded-[2.5rem] border border-ows-teal/5 bg-ows-grey animate-pulse">
                <div className="flex justify-between mb-8">
                  <div className="h-8 bg-ows-dark/10 rounded w-24"></div>
                  <div className="h-6 bg-ows-dark/10 rounded-full w-16"></div>
                </div>
                <div className="h-4 bg-ows-dark/10 rounded w-32 mb-4"></div>
                <div className="h-10 bg-ows-dark/10 rounded w-full mb-8"></div>
                <div className="flex justify-between mb-8">
                  <div className="h-6 bg-ows-dark/10 rounded w-16"></div>
                  <div className="h-6 bg-ows-dark/10 rounded w-20"></div>
                </div>
                <div className="h-14 bg-ows-dark/5 rounded-full w-full"></div>
              </div>
            ))}
          </>
        ) : slots.length === 0 ? (
          <div className="col-span-full py-24 text-center border border-dashed border-ows-teal/10 rounded-3xl">
            <p className="text-ows-dark/20 font-serif italic text-lg tracking-widest uppercase">No upcoming sessions scheduled. Check back soon.</p>
          </div>
        ) : (
          slots.map((slot) => {
            const requiresElite = slot.type === 'baja' || slot.type === 'lake-crossing';
            const wResult = weatherData[slot.date];
            const theme = getSlotTheme(slot, wResult);
            const isMember = isSyndicateMember || isCorporateMember || isEliteMember;

            let cannotBook = false;
            let cannotBookReason = '';

            if (requiresElite && !isEliteMember) {
              cannotBook = true;
              cannotBookReason = 'Elite Rank Required';
            } else if ((theme.level === 'red' || theme.level === 'black') && !isEliteMember) {
              cannotBook = true;
              cannotBookReason = 'Elite Rank Required';
            } else if (theme.level === 'brown' && !isMember) {
              cannotBook = true;
              cannotBookReason = 'Syndicate Member Required';
            }

            return (
              <motion.div
                key={slot.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`p-10 rounded-[2.5rem] border transition-all duration-500 relative overflow-hidden ${theme.card}`}
              >
                {slot.type === 'night-raid' && (
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent pointer-events-none" />
                )}
                <div className="flex flex-col-reverse lg:flex-row lg:items-center justify-between gap-4 mb-8 mt-2 relative z-10 w-full">
                  <div className={`text-4xl font-display font-light uppercase tracking-widest ${theme.text}`}>
                    {new Date(slot.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  {theme.indicator && (
                    <div className={`px-4 py-2 flex items-center gap-2 rounded-full border ${theme.pill} text-[10px] tracking-widest font-mono uppercase shadow-sm self-start shrink-0`}>
                      <div className="flex -space-x-1">{theme.indicator}</div>
                      <span>{theme.levelLabel}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-end mb-6 relative z-10">
                  <div className={`text-sm font-mono font-medium ${theme.textDim}`}>
                    {(() => {
                      const [hours, minutes] = slot.time.split(':');
                      const h = parseInt(hours);
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      const displayH = h % 12 || 12;
                      const duration = getDuration(slot.type);
                      const endH = (h + parseInt(duration.split(' ')[0] || "4")) % 24;
                      const endAmpm = endH >= 12 ? 'PM' : 'AM';
                      const displayEndH = endH % 12 || 12;
                      return `${displayH}:${minutes} ${ampm} - ${displayEndH}:${minutes} ${endAmpm}`;
                    })()}
                  </div>
                  <div className={`text-[11px] uppercase tracking-widest font-mono ${theme.textDim}`}>
                    {getDuration(slot.type)} {(slot.type || 'night-raid').replace('-', ' ')}
                  </div>
                </div>

                {/* Weather Indicator */}
                <div className="mb-8 relative z-10 flex items-center min-h-[30px]">
                  {(() => {
                    if (!wResult) return null;
                    if (wResult.loading) {
                      return <div className={`text-[9px] uppercase font-mono tracking-widest flex items-center gap-2 ${theme.textDim}`}><Loader2 className="h-3 w-3 animate-spin" /> Fetching Marine Conditions...</div>;
                    }
                    if (wResult.status === 'pending') {
                      return <div className={`text-[9px] uppercase font-mono tracking-widest flex items-center gap-2 ${theme.textDim}`}><Cloud className="h-3 w-3" /> Marine Forecast: Pending (T-7 Days)</div>;
                    }
                    if (wResult.status === 'success') {
                      return (
                        <div className={`flex flex-wrap gap-2 text-[10px] sm:text-xs font-bold font-mono tracking-widest uppercase mt-2`}>
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl shadow-sm border border-ows-teal/20 transition-all hover:scale-105 text-ows-teal ${theme.card.includes('bg-ows-grey') ? 'bg-ows-teal/5' : 'bg-ows-teal/15'}`}>
                            <ThermometerSun className="h-4 w-4" /> {wResult.airTemperature != null ? `${Math.round(wResult.airTemperature)}°C` : 'N/A'}
                          </div>
                          {(wResult.waterTemperature != null && !isNaN(wResult.waterTemperature) || wResult.waveHeight != null && !isNaN(wResult.waveHeight)) && (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl shadow-sm border border-blue-500/20 transition-all hover:scale-105 text-blue-500 ${theme.card.includes('bg-ows-grey') ? 'bg-blue-500/5' : 'bg-blue-500/15'}`}>
                              <Waves className="h-4 w-4" />
                              {wResult.waterTemperature != null && !isNaN(wResult.waterTemperature) ? `${Math.round(wResult.waterTemperature)}°C` : ''}
                              {wResult.waterTemperature != null && !isNaN(wResult.waterTemperature) && wResult.waveHeight != null && !isNaN(wResult.waveHeight) ? ' // ' : ''}
                              {wResult.waveHeight != null && !isNaN(wResult.waveHeight) ? `${Number(wResult.waveHeight).toFixed(1)}m wave` : ''}
                            </div>
                          )}
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl shrink-0 shadow-sm border border-indigo-500/20 transition-all hover:scale-105 text-indigo-500 ${theme.card.includes('bg-ows-grey') ? 'bg-indigo-500/5' : 'bg-indigo-500/15'}`}>
                            <Wind className="h-4 w-4" />
                            {wResult.windSpeed != null ? `${Math.round(wResult.windSpeed * 3.6)} km/h` : 'N/A'}
                            {wResult.windDirection != null ? ` ${['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(wResult.windDirection / 45) % 8]}` : ''}
                          </div>
                        </div>
                      );
                    }
                    return <div className={`text-[9px] uppercase font-mono tracking-widest flex items-center gap-2 ${theme.textDim}`}><AlertCircle className="h-3 w-3 opacity-50" /> Radar Offline</div>;
                  })()}
                </div>

                {/* Location display inside card */}
                {slot.location && (
                  <div className="mb-6 relative z-10 p-3.5 bg-ows-grey/35 rounded-2xl border border-ows-teal/5 flex flex-col gap-0.5">
                    <div className={`flex items-center gap-1.5 text-[10px] sm:text-xs font-mono font-bold uppercase tracking-widest ${theme.text}`}>
                      <MapPin className="h-4 w-4 text-ows-teal shrink-0" /> Launch: {slot.location}
                    </div>
                    {slot.exactMeetingLocation && (
                      <div className={`text-[9px] font-mono uppercase tracking-widest pl-5 ${theme.textDim}`}>
                        Meet: {slot.exactMeetingLocation}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-4 mb-10 relative z-10">
                  <div className={`flex items-center justify-between text-[10px] font-medium tracking-widest uppercase font-mono ${theme.textDim}`}>
                    <span>Availability</span>
                    <span className={slot.booked >= slot.capacity ? 'text-ows-orange' : ''}>
                      {slot.capacity - slot.booked} Slots Left
                    </span>
                  </div>
                  <div className={`w-full h-1 rounded-full overflow-hidden ${theme.barBg}`}>
                    <div
                      className={`h-full transition-all duration-1000 ${slot.booked >= slot.capacity ? 'bg-ows-orange' : theme.barFill}`}
                      style={{ width: `${(slot.booked / slot.capacity) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-10 relative z-10">
                  <div className="flex flex-col">
                    <div className={`text-2xl font-display flex items-baseline gap-2 ${theme.text}`}>
                      {calculateDiscountedPrice(slot.price, slot.type || '') < slot.price ? (
                        <>
                          <span className={theme.text}>${calculateDiscountedPrice(slot.price, slot.type || '')}</span>
                          {!isEliteMember && (
                            <span className={`text-xs line-through font-mono decoration-ows-orange/30 ${theme.textDim}`}>${slot.price}</span>
                          )}
                        </>
                      ) : (
                        `$${slot.price}`
                      )}
                    </div>
                    <div className={`text-[8px] font-bold uppercase tracking-widest mt-1 font-mono ${theme.textDim}`}>
                      + 13% HST
                    </div>
                    {isSyndicateMember && (slot.type === 'group-lesson' || slot.type === 'coaching' || slot.type === 'lake-crossing' || slot.type === 'night-raid' || (slot.type as string) === 'night-exped' || (slot.type as string) === 'day' || (slot.type as string) === 'night') && (
                      <div className="text-[8px] font-bold uppercase tracking-widest mt-1 font-mono text-ows-teal">
                        50% Syndicate Discount Applied
                      </div>
                    )}
                    {isEliteMember && (slot.type !== 'baja' && slot.type !== 'team-building' && slot.type !== 'corporate') && (
                      <div className="text-[8px] font-bold uppercase tracking-widest mt-1 font-mono text-ows-orange">
                        Included Free with Elite Rank
                      </div>
                    )}
                  </div>
                  {getMembershipBadge() && (
                    <div className={`px-2 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest font-mono ${getMembershipBadge()?.color}`}>
                      {getMembershipBadge()?.label}
                    </div>
                  )}
                </div>

                <button
                  disabled={isBooked(slot.id!) || isWaitlisted(slot.id!) || bookingStatus.status === 'loading' || cannotBook}
                  onClick={() => {
                    if ((slot as any).isTeaser && isEliteMember) {
                      window.location.href = "mailto:operations@openwatersyndicate.com?subject=Elite Booking Request";
                      return;
                    }
                    setSelectedSlotId(slot.id!);
                    setBookingStep('info');
                  }}
                  className={`w-full py-5 rounded-full font-bold text-[10px] tracking-[0.3em] uppercase transition-all flex items-center justify-center gap-3 font-mono ${isBooked(slot.id!)
                    ? 'bg-ows-teal/10 text-ows-teal border border-ows-teal/20'
                    : isWaitlisted(slot.id!)
                      ? 'bg-ows-orange/10 text-ows-orange border border-ows-orange/20 cursor-not-allowed'
                      : cannotBook
                        ? 'bg-ows-dark/5 text-ows-dark/40 cursor-not-allowed border border-ows-dark/10'
                        : (slot.status === 'full' || slot.booked >= slot.capacity)
                          ? 'bg-ows-orange text-white hover:bg-ows-orange/80'
                          : theme.button
                    }`}
                >
                  {isBooked(slot.id!) ? (
                    <>
                      <Check className="h-4 w-4" />
                      Confirmed
                    </>
                  ) : isWaitlisted(slot.id!) ? (
                    <>
                      <Clock className="h-4 w-4" />
                      On Waitlist
                    </>
                  ) : (slot.status === 'full' || slot.booked >= slot.capacity) ? (
                    <>
                      Join Waitlist
                      <ArrowRight className="h-4 w-4" />
                    </>
                  ) : cannotBook ? (
                    cannotBookReason
                  ) : ((slot as any).isTeaser && isEliteMember) ? (
                    'Contact Concierge'
                  ) : (
                    <>
                      Reserve Spot
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </motion.div>
            );
          })
        )}
      </div>

      {type === 'team-building' && (
        <div className="mt-32 space-y-24">
          {/* Weather Guarantee */}
          <div className="max-w-3xl mx-auto text-center px-6">
            <div className="inline-block px-4 py-1 bg-ows-teal/5 rounded-full border border-ows-teal/10 mb-6">
              <h4 className="text-[8px] font-bold uppercase tracking-[0.3em] text-ows-teal font-mono">
                THE OWS WEATHER GUARANTEE
              </h4>
            </div>
            <p className="text-[10px] text-ows-dark/40 uppercase tracking-[0.1em] leading-relaxed max-w-2xl mx-auto font-medium">
              Lake Ontario dictates our timeline. If your scheduled launch window is compromised by unsafe wind, lightning, or significant harbor chop, your booking remains 100% secure. We will instantly coordinate with your Lead Operator to unlock an alternative morning or afternoon time slot that fits your company's calendar.
            </p>
          </div>

          {/* Packing Manual */}
          <div className="p-12 md:p-20 bg-ows-grey rounded-[3rem] border border-ows-teal/5 max-w-5xl mx-auto shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-[0.4em] text-ows-dark mb-12 font-mono flex items-center gap-4">
              <Package className="h-4 w-4 text-ows-teal" />
              WHAT YOUR COHORT MUST BRING
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 text-[10px] text-ows-dark/50 font-medium leading-relaxed uppercase tracking-wider">
              <div className="flex gap-6">
                <span className="text-ows-teal shrink-0 text-sm">✔</span>
                <p>
                  <span className="font-bold text-ows-dark block mb-2 tracking-tight">CLEAN HYDRATION</span>
                  High-capacity water or electrolyte fluids in stainless steel or glass flasks. Zero single-use plastic bottles are permitted on the vessel fleet.
                </p>
              </div>
              <div className="flex gap-6">
                <span className="text-ows-teal shrink-0 text-sm">✔</span>
                <p>
                  <span className="font-bold text-ows-dark block mb-2 tracking-tight">PHYSICAL UV PROTECTION</span>
                  Long-sleeve UPF 50+ technical rash guards, wide-brim hats, and polarized eyewear to negate intense water reflection. If topicals are absolutely required, non-nano mineral zinc or titanium oxide shields only—no toxic synthetic chemical filters.
                </p>
              </div>
              <div className="flex gap-6">
                <span className="text-ows-teal shrink-0 text-sm">✔</span>
                <p>
                  <span className="font-bold text-ows-dark block mb-2 tracking-tight">OPERATIONAL GEAR</span>
                  A personal towel, a complete change of dry clothing, and a secure dry bag for mobile communication devices and vehicle keys.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {selectedSlotId && selectedSlot && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedSlotId(null)}
              className="absolute inset-0 bg-ows-dark/40 backdrop-blur-md"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative bg-white border border-ows-teal/10 w-full max-w-2xl rounded-[3rem] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
            >
              <div className="p-8 border-b border-ows-teal/5 flex justify-between items-center bg-white sticky top-0 z-10">
                <div>
                  <h3 className="text-2xl font-display font-light text-ows-dark uppercase tracking-widest">
                    {selectedSlot.type.replace('-', ' ')} Booking
                  </h3>
                  <p className="text-[10px] font-medium text-ows-teal uppercase tracking-widest mt-1 font-mono">
                    {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="text-[10px] font-medium text-ows-teal uppercase tracking-widest mt-1 font-mono">
                    Window: {(() => {
                      const [hours, minutes] = selectedSlot.time.split(':');
                      const h = parseInt(hours);
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      const displayH = h % 12 || 12;
                      const duration = getDuration(selectedSlot.type);
                      const [durationVal, durationUnit] = duration.split(' ');
                      const endH = (h + parseInt(durationVal || "4")) % 24;
                      const endAmpm = endH >= 12 ? 'PM' : 'AM';
                      const displayEndH = endH % 12 || 12;
                      return `${displayH}:${minutes} ${ampm} - ${displayEndH}:${minutes} ${endAmpm} (${duration})`;
                    })()}
                  </p>
                </div>
                <button onClick={() => setSelectedSlotId(null)} className="text-ows-dark/40 hover:text-ows-dark transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div ref={modalScrollRef} className="p-8 overflow-y-auto flex-grow space-y-12">
                {bookingStep === 'info' && (
                  <div className="space-y-12">
                    {selectedSlot.type === 'baja' ? (
                      <div className="space-y-8">
                        <div className="p-6 bg-ows-sand rounded-3xl border border-ows-teal/10">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-ows-teal mb-4 font-mono">10-Day Mission Profile</h4>
                          <ul className="space-y-3 text-xs text-ows-dark/70 font-light">
                            <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ows-teal" /> 6 Day Open Water Expedition</li>
                            <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ows-teal" /> 2 Days Pre-launch Prep & Luxury Stay</li>
                            <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ows-teal" /> 2 Days Post-expedition Recovery & Luxury Stay</li>
                          </ul>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-ows-grey rounded-2xl border border-ows-teal/5">
                            <h5 className="text-[8px] font-bold uppercase tracking-widest text-ows-dark/40 mb-2">Included</h5>
                            <p className="text-[10px] text-ows-teal uppercase tracking-widest font-bold">Luxury House Housing</p>
                          </div>
                          <div className="p-4 bg-red-50/50 rounded-2xl border border-red-500/10">
                            <h5 className="text-[8px] font-bold uppercase tracking-widest text-red-500/40 mb-2">Excluded</h5>
                            <p className="text-[10px] text-red-500/60 uppercase tracking-widest font-bold">Airfare & Food</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 text-ows-teal">
                            <Package className="h-5 w-5" />
                            <h4 className="text-xs font-bold uppercase tracking-widest font-mono">What to Bring</h4>
                          </div>
                          <ul className="space-y-4 text-sm text-ows-dark/60">
                            {selectedSlot.type === 'team-building' ? (
                              <>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal" /> Clean Hydration (No Plastic)</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal" /> UPF 50+ Rash Guard & Polarized Optics</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal" /> Dry Clothes & Towel</li>
                              </>
                            ) : (
                              <>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal" /> Comfortable activewear</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal" /> Waterproof phone case</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal" /> Change of clothes</li>
                              </>
                            )}
                          </ul>
                        </div>
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 text-ows-teal">
                            <Waves className="h-5 w-5" />
                            <h4 className="text-xs font-bold uppercase tracking-widest font-mono">What to Expect</h4>
                          </div>
                          <ul className="space-y-4 text-sm text-ows-dark/60">
                            {selectedSlot.type === 'team-building' ? (
                              <>
                                <li className="flex items-start gap-3">
                                  <Check className="h-4 w-4 text-ows-teal mt-0.5 shrink-0" />
                                  <div>
                                    <span className="block font-bold">SESSION // THE ISLANDS LOOP</span>
                                    <span className="block text-xs mt-1 text-ows-dark/60 leading-relaxed uppercase tracking-tighter">High-performance marine exploration through Toronto's inner island channels.</span>
                                  </div>
                                </li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal shrink-0" /> Exclusive Fleet Deployment</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal shrink-0" /> Professional instructor Supervision</li>
                              </>
                            ) : selectedSlot.type === 'coaching' ? (
                              <>
                                <li className="flex items-start gap-3">
                                  <Check className="h-4 w-4 text-ows-teal mt-0.5 shrink-0" />
                                  <div>
                                    <span className="block">Hardware Provisioned.</span>
                                    <span className="block text-xs mt-1 text-ows-dark/60">Your private booking includes full deployment of a premium OWS or partner paddle board, PFD, and paddle at the launch point.</span>
                                  </div>
                                </li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal shrink-0" /> Certified OWS Guides</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal shrink-0" /> Safety Briefing</li>
                              </>
                            ) : (
                              <>
                                <li className="flex items-start gap-3">
                                  <Check className="h-4 w-4 text-ows-teal mt-0.5 shrink-0" />
                                  <div>
                                    <span className="block">Gear Referral Protocols.</span>
                                    <span className="block text-xs mt-1 text-ows-dark/60">We focus on elite instruction. If you require a vessel, check the rental box below. We will notify our partners to secure your equipment with a 20% OWS-exclusive discount at the launch point.</span>
                                  </div>
                                </li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal shrink-0" /> Certified OWS Guides</li>
                                <li className="flex items-center gap-3"><Check className="h-3 w-3 text-ows-teal shrink-0" /> Safety Briefing</li>
                              </>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}

                    {selectedSlot.type === 'event' && (
                      <div className="space-y-6">
                        <div className="flex items-center gap-3 text-ows-teal">
                          <Check className="h-5 w-5" />
                          <h4 className="text-xs font-bold uppercase tracking-widest font-mono">Event Protocol</h4>
                        </div>
                        <p className="text-xs text-ows-dark/60 leading-relaxed italic">
                          "Syndicate community event. Open water coordination, gear demos, and multi-vessel formation training."
                        </p>
                      </div>
                    )}

                    {selectedSlot.type === 'lake-crossing' && (
                      <div className="p-6 bg-ows-teal/5 rounded-3xl border border-ows-teal/10">
                        <p className="text-xs text-ows-dark/60 leading-relaxed italic">
                          "Lake Ontario Crossing: A 14-hour endurance operation. Physical vetting required."
                        </p>
                      </div>
                    )}

                    {!isEliteMember && selectedSlot.type === 'baja' && (
                      <div className="p-6 bg-ows-sand rounded-3xl border border-ows-teal/10 flex items-center gap-6">
                        <Shield className="h-10 w-10 text-ows-orange" />
                        <div>
                          <h4 className="text-sm font-bold uppercase tracking-widest text-ows-dark font-mono">Elite Access Required</h4>
                          <p className="text-xs text-ows-dark/60 leading-relaxed mt-1">
                            This mission requires <span className="font-bold text-ows-orange">Elite Status</span> rank.
                          </p>
                        </div>
                      </div>
                    )}

                    {isEliteMember && selectedSlot.type === 'baja' && (
                      <div className="p-6 bg-ows-orange/5 rounded-3xl border border-ows-orange/20 flex items-center gap-6">
                        <Shield className="h-10 w-10 text-ows-orange" />
                        <div>
                          <h4 className="text-sm font-bold uppercase tracking-widest text-ows-orange font-mono">Elite Clearance</h4>
                          <p className="text-xs text-ows-orange/80 leading-relaxed mt-1">
                            Protocol verified. Your Elite rank grants you clearance to book this mission.
                          </p>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => setBookingStep('details')}
                      className="w-full py-6 bg-ows-dark text-white rounded-full font-bold text-[10px] tracking-[0.5em] uppercase hover:bg-ows-teal transition-all font-mono"
                    >
                      Continue to Details
                    </button>
                  </div>
                )}

                {bookingStep === 'details' && (
                  <div className="space-y-8">
                    <div className="p-6 bg-ows-sand/30 rounded-3xl border border-ows-teal/10 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal">Selected Mission</p>
                        <h4 className="text-sm font-bold text-ows-dark mt-1">{selectedSlot.type.replace('-', ' ')}</h4>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal">Date & Time</p>
                        <h4 className="text-sm font-bold text-ows-dark mt-1">
                          {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </h4>
                        <p className="text-[10px] font-bold text-ows-dark/60 mt-0.5 uppercase font-mono">
                          {(() => {
                            const [hours, minutes] = selectedSlot.time.split(':');
                            const h = parseInt(hours);
                            const ampm = h >= 12 ? 'PM' : 'AM';
                            const displayH = h % 12 || 12;
                            const duration = getDuration(selectedSlot.type);
                            const endH = (h + parseInt(duration.split(' ')[0] || "4")) % 24;
                            const endAmpm = endH >= 12 ? 'PM' : 'AM';
                            const displayEndH = endH % 12 || 12;
                            return `${displayH}:${minutes} ${ampm} - ${displayEndH}:${minutes} ${endAmpm}`;
                          })()} ({getDuration(selectedSlot.type).toUpperCase()})
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {selectedSlot.type === 'team-building' ? (
                        <>
                          <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-2 font-mono">
                            <Shield className="h-3 w-3" /> Corporate Entity Name
                          </label>
                          <input
                            type="text"
                            value={formData.corporateName || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, corporateName: e.target.value }))}
                            className="w-full px-6 py-4 rounded-2xl bg-ows-grey border border-ows-teal/5 focus:border-ows-teal transition-all text-sm outline-none"
                            placeholder="e.g. Bank of Montreal / Deloitte Innovation Lab"
                          />
                        </>
                      ) : null}
                    </div>

                    <div className="space-y-4">
                      <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-2 font-mono">
                        <User className="h-3 w-3" /> {selectedSlot.type === 'team-building' ? 'Lead Operator Full Name' : 'Full Name'}
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-6 py-4 rounded-2xl bg-ows-grey border border-ows-teal/5 focus:border-ows-teal transition-all text-sm outline-none"
                        placeholder={selectedSlot.type === 'team-building' ? 'Primary point of contact' : 'Enter your name'}
                      />
                    </div>
                    <div className="space-y-4">
                      <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 ml-2 font-mono">
                        <Mail className="h-3 w-3" /> {selectedSlot.type === 'team-building' ? 'Corporate Email Address' : 'Email Address'}
                      </label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-6 py-4 rounded-2xl bg-ows-grey border border-ows-teal/5 focus:border-ows-teal transition-all text-sm outline-none"
                        placeholder="For automated dispatch of coordinates"
                      />
                    </div>
                    <div className="space-y-4">
                      <label className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-ows-dark/40 ml-2 font-mono">
                        <Phone className="h-3 w-3" /> {selectedSlot.type === 'team-building' ? 'Direct Contact Mobile Number' : 'Phone Number'}
                      </label>
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-6 py-4 rounded-2xl bg-ows-grey border border-ows-teal/5 focus:border-ows-teal transition-all text-sm outline-none"
                        placeholder="Essential for real-time weather updates"
                      />
                    </div>

                    {!currentUser && (
                      <div className="space-y-4 pt-4">
                        <label className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-ows-teal ml-2 font-mono">
                          <Shield className="h-3 w-3" /> Account Creation (Optional Free Member Tier)
                        </label>
                        <input
                          type="password"
                          value={formData.password || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-6 py-4 rounded-2xl bg-ows-grey border border-ows-teal/5 focus:border-ows-teal transition-all text-sm outline-none"
                          placeholder="Password (minimum 6 characters)"
                        />
                        <p className="text-[9px] font-mono uppercase tracking-widest text-ows-dark/40 ml-2">
                          Create an account for the Prospect tier to track progress and manage bookings. Unlocking a free membership.
                        </p>
                      </div>
                    )}

                    {(() => {
                      const isBoardIncluded = selectedSlot.type === 'coaching' || selectedSlot.type === 'team-building';

                      if (isBoardIncluded) {
                        return (
                          <div className="space-y-4 pt-4">
                            <div className="flex items-start gap-4 p-5 bg-ows-teal/5 rounded-2xl border border-ows-teal/10">
                              <div className="w-5 h-5 shrink-0 rounded bg-ows-teal/10 flex items-center justify-center mt-0.5">
                                <Check className="h-3 w-3 text-ows-teal" />
                              </div>
                              <div className="space-y-1">
                                <span className="text-[9px] font-bold uppercase tracking-widest text-ows-teal block font-mono">Premium Paddle Board Included</span>
                                <span className="text-[10px] text-ows-dark/60 leading-relaxed block">
                                  For one-on-one sessions and corporate bookings, high-end OWS and partner hardware is fully provided at the launch point with no additional charge.
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-4 pt-4">
                          <label className="flex items-start gap-4 p-5 bg-ows-grey/50 rounded-2xl border border-ows-teal/5 cursor-pointer hover:bg-ows-teal/5 transition-all group">
                            <div className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center mt-0.5 transition-all ${formData.requiresGear ? 'bg-ows-teal border-ows-teal' : 'border-ows-teal/20 group-hover:border-ows-teal'}`}>
                              {formData.requiresGear && <Check className="h-3 w-3 text-white" />}
                            </div>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={formData.requiresGear}
                              onChange={(e) => setFormData(prev => ({ ...prev, requiresGear: e.target.checked }))}
                            />
                            <div className="space-y-1">
                              <span className="text-[9px] font-bold uppercase tracking-widest text-ows-dark block">Add Paddle Board Rental + $50</span>
                              {isSyndicateMember && (
                                <span className="text-[10px] text-ows-teal font-bold uppercase tracking-widest block font-mono">50% Syndicate Discount Applied in Checkout</span>
                              )}
                              <span className="text-[10px] text-ows-dark/40 leading-relaxed block">Premium inflatable paddle board, PFD, and paddle provided at launch point.</span>
                            </div>
                          </label>
                        </div>
                      );
                    })()}

                    <div className="pt-4">
                      <button
                        type="button"
                        onClick={() => setUseGiftCard(!useGiftCard)}
                        className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ows-teal hover:text-ows-dark transition-colors font-mono"
                      >
                        <Package className="h-3 w-3" /> {useGiftCard ? 'Cancel Gift Card' : 'Have a Gift Card?'}
                      </button>

                      <AnimatePresence>
                        {useGiftCard && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden mt-4"
                          >
                            <input
                              type="text"
                              value={formData.giftCode || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, giftCode: e.target.value.toUpperCase() }))}
                              className="w-full px-6 py-4 rounded-2xl bg-white border border-ows-teal/20 focus:border-ows-teal transition-all text-sm outline-none font-mono uppercase tracking-widest"
                              placeholder="OWS-XXXXXXXX"
                            />
                            <p className="text-[9px] text-ows-dark/40 mt-2 ml-2 uppercase tracking-tighter">Enter your 10-character gift card code</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="pt-2">
                      <div className="p-4 rounded-2xl border border-ows-teal/10 bg-ows-teal/5 flex items-center gap-3">
                        <Shield className="h-4 w-4 text-ows-teal shrink-0" />
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal font-mono">Any Elite equipment provided requires a sign-off.</p>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-8">
                      <button
                        onClick={() => setBookingStep('info')}
                        className="flex-1 py-6 bg-ows-grey text-ows-dark/40 rounded-full font-medium text-[10px] tracking-[0.5em] uppercase hover:bg-ows-dark/5 transition-all font-mono"
                      >
                        Back
                      </button>
                      <button
                        disabled={!formData.name || !formData.email || !formData.phone || (selectedSlot.type === 'team-building' && !formData.corporateName)}
                        onClick={() => setBookingStep('waiver')}
                        className="flex-[2] py-6 bg-ows-dark text-white rounded-full font-medium text-[10px] tracking-[0.5em] uppercase hover:bg-ows-teal transition-all font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continue to Waiver
                      </button>
                    </div>
                  </div>
                )}

                {bookingStep === 'waiver' && (
                  <div className="space-y-8">
                    <div className="p-6 bg-ows-sand/30 rounded-3xl border border-ows-teal/10 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal">Selected Mission</p>
                        <h4 className="text-sm font-bold text-ows-dark mt-1">{selectedSlot.type.replace('-', ' ')}</h4>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal">Date & Time</p>
                        <h4 className="text-sm font-bold text-ows-dark mt-1">
                          {new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </h4>
                        <p className="text-[10px] font-bold text-ows-dark/60 mt-0.5 uppercase font-mono">
                          {(() => {
                            const [hours, minutes] = selectedSlot.time.split(':');
                            const h = parseInt(hours);
                            const ampm = h >= 12 ? 'PM' : 'AM';
                            const displayH = h % 12 || 12;
                            const duration = getDuration(selectedSlot.type);
                            const endH = (h + parseInt(duration.split(' ')[0] || "4")) % 24;
                            const endAmpm = endH >= 12 ? 'PM' : 'AM';
                            const displayEndH = endH % 12 || 12;
                            return `${displayH}:${minutes} ${ampm} - ${displayEndH}:${minutes} ${endAmpm}`;
                          })()} ({getDuration(selectedSlot.type).toUpperCase()})
                        </p>
                      </div>
                    </div>

                    {selectedSlot.type === 'team-building' ? (
                      <div className="space-y-4">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-ows-dark ml-2 font-mono">Ironclad Maritime Liability Gates</h4>

                        <label className="flex items-start gap-4 p-5 bg-ows-grey rounded-2xl border border-ows-teal/5 cursor-pointer hover:bg-ows-teal/5 transition-all group">
                          <div className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center mt-0.5 transition-all ${formData.maritimeRiskAccepted ? 'bg-ows-teal border-ows-teal' : 'border-ows-teal/20 group-hover:border-ows-teal'}`}>
                            {formData.maritimeRiskAccepted && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={formData.maritimeRiskAccepted}
                            onChange={(e) => setFormData(prev => ({ ...prev, maritimeRiskAccepted: e.target.checked }))}
                          />
                          <div className="space-y-1">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-ows-dark block">1. MARITIME RISK ACKNOWLEDGEMENT</span>
                            <span className="text-[10px] text-ows-dark/40 leading-relaxed block">I understand that navigating the Toronto Inner Harbour and entering open water via hybrid vessels involves active commercial vessel traffic, water taxi wakes, and variable wind/chop conditions. I assume full physical and operational responsibility for my 1–8 cohort participants.</span>
                          </div>
                        </label>

                        <label className="flex items-start gap-4 p-5 bg-ows-grey rounded-2xl border border-ows-teal/5 cursor-pointer hover:bg-ows-teal/5 transition-all group">
                          <div className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center mt-0.5 transition-all ${formData.physicalStandardsAccepted ? 'bg-ows-teal border-ows-teal' : 'border-ows-teal/20 group-hover:border-ows-teal'}`}>
                            {formData.physicalStandardsAccepted && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={formData.physicalStandardsAccepted}
                            onChange={(e) => setFormData(prev => ({ ...prev, physicalStandardsAccepted: e.target.checked }))}
                          />
                          <div className="space-y-1">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-ows-dark block">2. PHYSICAL STANDARDS & COMPLIANCE</span>
                            <span className="text-[10px] text-ows-dark/40 leading-relaxed block">I certify that all corporate participants are capable swimmers in open water and agree to strictly follow all commands, safety rules, and navigation routes dictated by the OWS Professional Instructor at all times during deployment.</span>
                          </div>
                        </label>

                        <label className="flex items-start gap-4 p-5 bg-ows-grey rounded-2xl border border-ows-teal/5 cursor-pointer hover:bg-ows-teal/5 transition-all group">
                          <div className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center mt-0.5 transition-all ${formData.meteorologicalAdaptabilityAccepted ? 'bg-ows-teal border-ows-teal' : 'border-ows-teal/20 group-hover:border-ows-teal'}`}>
                            {formData.meteorologicalAdaptabilityAccepted && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={formData.meteorologicalAdaptabilityAccepted}
                            onChange={(e) => setFormData(prev => ({ ...prev, meteorologicalAdaptabilityAccepted: e.target.checked }))}
                          />
                          <div className="space-y-1">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-ows-dark block">3. METEOROLOGICAL ADAPTABILITY CLAUSE</span>
                            <span className="text-[10px] text-ows-dark/40 leading-relaxed block">I acknowledge that OWS operates under a strict safety-first standard. If challenging Lake Ontario weather shifts or small craft warnings occur during our slot, the OWS Lead Instructor maintains absolute authority to pivot to protected inner island channels or dynamically reschedule our cohort to an alternative time slot at zero additional fee.</span>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <>
                        <div className="p-8 bg-ows-grey rounded-3xl border border-ows-teal/5 max-h-64 overflow-y-auto space-y-6">
                          <h4 className="text-sm font-display font-light uppercase tracking-widest text-ows-dark">Release of Liability & Waiver</h4>
                          {profile?.waiverSigned ? (
                            <div className="p-4 bg-ows-teal/10 rounded-2xl flex items-center gap-3">
                              <Check className="h-4 w-4 text-ows-teal shrink-0" />
                              <p className="text-[10px] text-ows-teal uppercase tracking-widest font-bold">Global Digital Waiver is active on your profile. You are cleared for deployment.</p>
                            </div>
                          ) : (
                            <div className="text-[10px] text-ows-dark/40 leading-relaxed space-y-4">
                              <p>I hereby acknowledge that open water expeditions involve inherent risks, including but not limited to: changing weather conditions, water hazards, and physical exertion.</p>
                              <p>I certify that I am in good physical health and possess the necessary skills to participate in this session. I agree to follow all safety instructions provided by OWS guides.</p>
                              <p>I release OWS Explorer and its affiliates from any liability for injury, loss, or damage incurred during the expedition.</p>
                            </div>
                          )}
                        </div>

                        {!profile?.waiverSigned && (
                          <label className="flex items-center gap-4 p-6 bg-ows-grey rounded-3xl border border-ows-teal/5 cursor-pointer hover:bg-ows-teal/5 transition-all group">
                            <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all ${formData.waiverAccepted ? 'bg-ows-teal border-ows-teal' : 'border-ows-teal/20 group-hover:border-ows-teal'}`}>
                              {formData.waiverAccepted && <Check className="h-4 w-4 text-white" />}
                            </div>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={formData.waiverAccepted}
                              onChange={(e) => setFormData(prev => ({ ...prev, waiverAccepted: e.target.checked }))}
                            />
                            <span className="text-[10px] font-medium uppercase tracking-widest text-ows-dark/60">I accept the terms of the waiver</span>
                          </label>
                        )}
                      </>
                    )}

                    {bookingStatus.status === 'error' && (
                      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-500 text-[10px] font-medium uppercase tracking-widest">
                        <AlertCircle className="h-4 w-4" />
                        {bookingStatus.message}
                      </div>
                    )}

                    <div className="flex gap-4">
                      <button
                        onClick={() => setBookingStep('details')}
                        className="flex-1 py-6 bg-ows-grey text-ows-dark/40 rounded-full font-medium text-[10px] tracking-[0.5em] uppercase hover:bg-ows-dark/5 transition-all font-mono"
                      >
                        Back
                      </button>
                      <button
                        disabled={
                          (selectedSlot.type === 'team-building'
                            ? (!formData.maritimeRiskAccepted || !formData.physicalStandardsAccepted || !formData.meteorologicalAdaptabilityAccepted)
                            : (!profile?.waiverSigned && !formData.waiverAccepted)
                          ) || bookingStatus.status === 'loading'
                        }
                        onClick={handleBook}
                        className="flex-[2] py-6 bg-ows-dark text-white rounded-full font-medium text-[10px] tracking-[0.5em] uppercase hover:bg-ows-teal transition-all font-mono disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-xl hover:shadow-ows-teal/20"
                      >
                        {(() => {
                          let displayPrice = calculateDiscountedPrice(selectedSlot.price, selectedSlot.type);
                          const isBoardIncluded = selectedSlot.type === 'coaching' || selectedSlot.type === 'team-building';
                          if (formData.requiresGear && !isBoardIncluded) {
                            displayPrice += isSyndicateMember ? 25 : 50;
                          }

                          if (bookingStatus.status === 'loading') {
                            return <span className="animate-pulse">Authorizing...</span>;
                          }
                          if (bookingStatus.status === 'success') {
                            return <><Check className="h-4 w-4" /> Confirmed</>;
                          }
                          if (selectedSlot.status === 'full' || selectedSlot.booked >= selectedSlot.capacity) {
                            return 'Join Waitlist (No Charge)';
                          }
                          if (useGiftCard) {
                            return 'Redeem Gift Card';
                          }
                          if (displayPrice === 0) {
                            return 'Confirm Free Reserve';
                          }

                          return `Secure Authorization — $${(displayPrice * 1.13).toFixed(2)}`;
                        })()}
                      </button>
                    </div>
                  </div>
                )}

                {bookingStep === 'redirect' && (
                  <div className="py-12 space-y-8 text-center flex flex-col items-center">
                    <div className="w-20 h-20 bg-ows-teal/10 rounded-full flex items-center justify-center animate-bounce">
                      <Shield className="h-10 w-10 text-ows-teal" />
                    </div>
                    <div className="space-y-4">
                      <h4 className="text-xl font-display font-light uppercase tracking-widest text-ows-dark">Mission Authorization</h4>
                      <p className="text-[10px] text-ows-dark/40 uppercase tracking-widest leading-relaxed max-w-sm mx-auto">
                        Secure gateway initialized. If the authorization terminal doesn't open automatically, use the manual protocol below.
                      </p>
                    </div>
                    <div className="flex flex-col items-center gap-6 w-full max-w-xs">
                      {checkoutUrl && (
                        <div className="w-full space-y-4">
                          <button
                            onClick={() => {
                              window.location.href = checkoutUrl;
                            }}
                            className="w-full py-5 bg-ows-teal text-white rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-ows-dark transition-all flex items-center justify-center gap-3 shadow-xl shadow-ows-teal/20"
                          >
                            Open Payment Terminal
                            <ArrowRight className="h-4 w-4" />
                          </button>

                          <a
                            href={checkoutUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-[9px] text-ows-dark/30 uppercase tracking-widest hover:text-ows-teal transition-colors underline"
                          >
                            Open in New Tab (Last Resort)
                          </a>
                        </div>
                      )}

                      {!checkoutUrl && (
                        <div className="flex items-center gap-2 text-ows-teal">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-[8px] font-bold uppercase tracking-widest font-mono">Securing Connection...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
